import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardMedia,
    Checkbox,
    Chip,
    Divider,
    Icon,
    Skeleton,
    Stack,
    styled,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ProjectDto, ProjectSaleDto } from "../../../api/psc";
import { CardInformation, PhotoLoader } from "../../../components/exports";
import {
    DefaultDarkProjectImagePath,
    DefaultProjectImagePath,
    GetProjectStatus,
} from "../../../constants/ProjectConstants";
import { SubscriptionPageInfo } from "../../../constants/SubscriptionConstants";
import { DialogContext } from "../../../contexts/DialogContext";
import { toggleProjectSelection } from "../../../slices/SharedSlice";
import { RootState } from "../../../slices/Store";
import {
    projectCheckboxContainerStyle,
    projectInfoIconStyle,
} from "../../../styles/ProjectDefaultStyles";
import { getPhaseLastUpdateDateDiff, isHaltProject } from "../../../utils/date";
import ProjectDetailDialog from "../../project-detail/ProjectDetailDialog";
import SaleActivityItem from "../../sales/components/SaleActivityItem";
import AddSaleActivityDialog from "../../sales/dialogs/AddSaleActivityDialog";

const ProjectTypeBox = styled(Box)(() => ({
    bgcolor: "background.paper",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
    pl: "8px",
    pr: "8px",
    py: "8px",
}));

// updated
export default function ProjectItem(props: {
    index?: number;
    project: ProjectDto;
    sale?: ProjectSaleDto;
    isLoading: boolean;
}) {
    const dispatch = useDispatch();
    const theme = useTheme();

    const navigate = useNavigate();

    const sharedState = useSelector((state: RootState) => state.shared);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        setImageLoaded(false);
    }, [props.isLoading]);

    const project = props.project;
    const projectPlaceHolder =
        theme.palette.mode === "light"
            ? DefaultProjectImagePath
            : DefaultDarkProjectImagePath;
    const photoUrl = props.isLoading
        ? projectPlaceHolder
        : project.photo?.imageThumbnailUrl ?? projectPlaceHolder;

    const city = sharedState.baseInfo.cities.find(
        (c) => c.id === project.cityId
    );
    const region = city?.regions.find((c) => c.id === project.regionId);
    const subRegion = region?.subregions.find(
        (c) => c.id === project.subRegionId
    );
    const phase = sharedState.baseInfo.phaseList.find(
        (c) => c.id === project.phaseId
    );
    const labelConfig = GetProjectStatus(project);

    const requiredSubscriptionType = useMemo(
        () =>
            SubscriptionPageInfo.find(
                (s) => s.type === project.requiredSubscriptionType
            ),
        [project.requiredSubscriptionType]
    );

    const addressFirstSection = project.address?.split("-")[0];

    const { openDialog } = useContext(DialogContext);

    const openProjectModal = () => {
        if (props.isLoading) {
            return;
        }
        openDialog(
            <ProjectDetailDialog
                index={props.index}
                projectHashId={props.project.hashId}
                openSales={props.sale !== undefined}
                requirementId={props.project.requirement?.id}
            />,
            () => navigate(`#`)
        );
    };

    const openProjectWithSalesDialog = (e) => {
        e.stopPropagation();
        openDialog(
            <ProjectDetailDialog
                projectHashId={props.project.hashId}
                openSales={true}
            />,
            () => navigate("#")
        );
    };

    const openCreateSaleDialog = (e) => {
        e.stopPropagation();
        openDialog(<AddSaleActivityDialog saleId={props.sale.sale.id} />);
    };

    const openProjectWithNotesDialog = (e) => {
        e.stopPropagation();
        openDialog(
            <ProjectDetailDialog
                projectHashId={props.project.hashId}
                openNotes
            />,
            () => navigate("#")
        );
    };

    const isSelectedForBatchOperation = sharedState.batchOperationProjects.some(
        (i) => i.hashId === props.project.hashId
    );

    const toggleBatchOperationSelect = () => {
        dispatch(toggleProjectSelection(props.project));
    };

    const findTitleOfTheChip = (businessLineId: number) => {
        return (
            sharedState.baseInfo.requirementBusinessLines.find(
                (rbl) => rbl.id === businessLineId
            )?.title ?? "نامشخص"
        );
    };

    const IsLastUpdateDateMoreThanMonth = useMemo(() => {
        const res = getPhaseLastUpdateDateDiff(project.phaseLastUpdateDate);
        if (res.type === "month") {
            return true;
        }

        return false;
    }, [project.phaseLastUpdateDate]);

    return (
        <Card sx={{ opacity: props.isLoading ? "0.75" : "1", height: "100%" }}>
            <Stack sx={{ position: "relative", height: "100%" }}>
                <Stack
                    sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        right: 12,
                        zIndex: 1,
                    }}
                    direction="row"
                    spacing={1}
                >
                    {/* project id and status */}
                    <Chip
                        sx={{
                            backgroundColor: props.isLoading
                                ? "background.default"
                                : labelConfig.color,
                            color: "white",
                        }}
                        label={
                            <Typography sx={{ fontSize: 12 }}>
                                {props.isLoading ? (
                                    <Skeleton
                                        sx={{ display: "inline-block" }}
                                        width={40}
                                    />
                                ) : (
                                    project.hashId
                                )}{" "}
                                |{" "}
                                {props.isLoading ? (
                                    <Skeleton
                                        sx={{ display: "inline-block" }}
                                        width={60}
                                    />
                                ) : (
                                    labelConfig.title
                                )}
                            </Typography>
                        }
                    />

                    <Box sx={{ flexGrow: 1 }} />

                    {!props.isLoading && !props.sale && (
                        <Box sx={projectCheckboxContainerStyle}>
                            <Checkbox
                                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                                checked={isSelectedForBatchOperation}
                                onChange={toggleBatchOperationSelect}
                            />
                        </Box>
                    )}
                </Stack>

                <CardActionArea onClick={openProjectModal}>
                    <CardMedia sx={{ position: "relative" }}>
                        <Stack
                            sx={{
                                position: "absolute",
                                bottom: 12,
                                right: 12,
                                zIndex: 1,
                            }}
                            direction="row"
                            spacing={1}
                        >
                            {!props.isLoading && !project.requirement && (
                                <>
                                    <ProjectTypeBox>
                                        <img
                                            alt="bonyan"
                                            height={40}
                                            src={
                                                requiredSubscriptionType?.imageUrl
                                            }
                                        />
                                    </ProjectTypeBox>
                                </>
                            )}
                        </Stack>
                        <div
                            style={{ background: `url(${projectPlaceHolder})` }}
                        >
                            <PhotoLoader
                                src={photoUrl}
                                width={1024}
                                height={768}
                                style={{ width: "100%", height: "auto" }}
                            />
                        </div>

                        {!props.isLoading && (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: 16,
                                    right: 16,
                                    zIndex: 1,
                                }}
                            >
                                {/*can visit project*/}
                                {project.canVisit && (
                                    <Tooltip title="این پروژه برای حساب کاربری شما قابل مشاهده است">
                                        <Icon
                                            sx={{
                                                ...projectInfoIconStyle,
                                                color: "#00bf69",
                                            }}
                                        >
                                            check
                                        </Icon>
                                    </Tooltip>
                                )}

                                {/*has active constructor*/}
                                {project.hasAvailableConstructor && (
                                    <Tooltip title="این پروژه حاوی سازنده قابل دسترس است">
                                        <Icon
                                            sx={{
                                                ...projectInfoIconStyle,
                                                color: "#0099eb",
                                            }}
                                        >
                                            person
                                        </Icon>
                                    </Tooltip>
                                )}

                                {/*has notes*/}
                                {project.hasNote && (
                                    <Tooltip title="این پروژه حاوی یادداشت است">
                                        <Box
                                            onClick={openProjectWithNotesDialog}
                                        >
                                            <Icon
                                                sx={{
                                                    ...projectInfoIconStyle,
                                                    color: "#ff5900",
                                                }}
                                            >
                                                text_snippet
                                            </Icon>
                                        </Box>
                                    </Tooltip>
                                )}

                                {/*has followup*/}
                                {project.hasSale && (
                                    <Tooltip title="این پروژه فرایند فروش پیگیری است">
                                        <Box
                                            onClick={openProjectWithSalesDialog}
                                        >
                                            <Icon
                                                sx={{
                                                    ...projectInfoIconStyle,
                                                    color: "#8400ff",
                                                }}
                                            >
                                                receipt
                                            </Icon>
                                        </Box>
                                    </Tooltip>
                                )}

                                {/*project has halted*/}
                                {isHaltProject(project.phaseLastUpdateDate) && (
                                    <Tooltip title="این پروژه بیشتر از دو سال است که متوقف است">
                                        <Box
                                            onClick={openProjectWithSalesDialog}
                                        >
                                            <Icon
                                                color="error"
                                                sx={{ ...projectInfoIconStyle }}
                                            >
                                                block
                                            </Icon>
                                        </Box>
                                    </Tooltip>
                                )}

                                <Box flexGrow={1} />

                                {project.requirement && (
                                    <Chip
                                        color="primary"
                                        label={findTitleOfTheChip(
                                            project.requirement.businessLineId
                                        )}
                                    />
                                )}
                            </Stack>
                        )}
                    </CardMedia>

                    <Box sx={{ p: 1.5 }}>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="nowrap"
                            divider={
                                <Typography
                                    fontSize="smaller"
                                    color="text.secondary"
                                >
                                    /
                                </Typography>
                            }
                        >
                            {props.isLoading ? (
                                <Skeleton
                                    sx={{ display: "inline-block" }}
                                    width={40}
                                />
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    noWrap
                                    fontSize="smaller"
                                >
                                    {city?.title}
                                </Typography>
                            )}
                            {props.isLoading ? (
                                <Skeleton
                                    sx={{ display: "inline-block" }}
                                    width={40}
                                />
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    noWrap
                                    fontSize="smaller"
                                >
                                    {region?.title}
                                </Typography>
                            )}
                            {props.isLoading ? (
                                <Skeleton
                                    sx={{ display: "inline-block" }}
                                    width={40}
                                />
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    noWrap
                                    fontSize="smaller"
                                >
                                    {addressFirstSection}
                                </Typography>
                            )}
                        </Stack>
                        <Typography noWrap sx={{ fontSize: 12 }}>
                            {props.isLoading ? (
                                <Skeleton />
                            ) : (
                                project.address || "بدون آدرس"
                            )}
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1}>
                            <CardInformation
                                title={
                                    props.isLoading ? (
                                        <Skeleton width={50} />
                                    ) : (
                                        "کارفرما"
                                    )
                                }
                                value={
                                    props.isLoading ? (
                                        <Skeleton width={70} />
                                    ) : (
                                        project.receptor
                                    )
                                }
                            />
                            <CardInformation
                                title={
                                    props.isLoading ? (
                                        <Skeleton width={40} />
                                    ) : (
                                        "مرحله"
                                    )
                                }
                                value={
                                    props.isLoading ? (
                                        <Skeleton width={40} />
                                    ) : (
                                        phase?.title
                                    )
                                }
                            />
                            <CardInformation
                                title={
                                    props.isLoading ? (
                                        <Skeleton width={50} />
                                    ) : (
                                        "تاریخ آخرین تغییر مرحله"
                                    )
                                }
                                value={
                                    props.isLoading ? (
                                        <Skeleton width={70} />
                                    ) : (
                                        <>
                                            {IsLastUpdateDateMoreThanMonth
                                                ? getPhaseLastUpdateDateDiff(
                                                      project.phaseLastUpdateDate
                                                  ).description + "پیش "
                                                : " کمتر از یک ماه"}
                                        </>
                                    )
                                }
                            />
                            <CardInformation
                                title={
                                    props.isLoading ? (
                                        <Skeleton width={80} />
                                    ) : (
                                        "طبقه / واحد"
                                    )
                                }
                                value={
                                    props.isLoading ? (
                                        <Skeleton width={50} />
                                    ) : (
                                        `${project.floors} / ${project.units}`
                                    )
                                }
                            />
                            <CardInformation
                                title={
                                    props.isLoading ? (
                                        <Skeleton width={90} />
                                    ) : (
                                        "متراژ / زیربنا"
                                    )
                                }
                                value={
                                    props.isLoading ? (
                                        <Skeleton width={80} />
                                    ) : (
                                        `${project.groundArea} / ${project.residentialArea}`
                                    )
                                }
                            />
                        </Stack>
                    </Box>
                </CardActionArea>

                {props.sale && (
                    <>
                        <Box>
                            <Divider />
                            <Stack divider={<Divider />}>
                                {props.sale.sale.activities.map((a) => {
                                    return (
                                        <SaleActivityItem
                                            key={a.id}
                                            saleId={props.sale.sale.id}
                                            activity={a}
                                        />
                                    );
                                })}
                            </Stack>
                        </Box>

                        <Box flexGrow={1} />

                        <Stack direction="row" spacing={2} sx={{ p: 1.5 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<Icon>visibility</Icon>}
                                size="small"
                                onClick={openProjectModal}
                            >
                                مشاهده بیشتر
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<Icon>add</Icon>}
                                size="small"
                                color="secondary"
                                onClick={openCreateSaleDialog}
                            >
                                افزودن فعالیت
                            </Button>
                        </Stack>
                    </>
                )}
            </Stack>
        </Card>
    );
}
